import { useMemo, useRef, useState } from "react";
import { useForm, type DefaultValues } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { buildSubmissionSchema, type FormDefinition } from "@colab/shared";
import { Button } from "@/components/ui/button";
import { FIELD_RENDERERS } from "@/features/portal/forms/field-renderers/field-registry";
import { submitEmbedForm, type EmbedFormRow } from "./use-embed-form";
import { TurnstileWidget, TURNSTILE_SITE_KEY } from "./turnstile-widget";

// Phase 7 sub-bucket 19 — embed renderer.
//
// Mirrors the legacy `components/shared/forms/FormRenderer.tsx` +
// `EmbedFormRenderer.tsx` (server-action pair) but submits via raw fetch with
// `credentials: 'omit'` so cross-origin iframes never accidentally include
// the parent app's auth cookie.
//
// File-kind fields are intentionally excluded — the legacy embed surface
// didn't support attachments (the 3-step upload requires a draft submission,
// which embed flows skip). If a published form contains a file field and is
// embedded, the field is rendered as a notice rather than the upload control.

export function EmbedFormRenderer({ form }: { form: EmbedFormRow }) {
  const definition = form.definition as FormDefinition;
  const slug = form.slug;

  const schema = useMemo(() => buildSubmissionSchema(definition), [definition]);

  // Field defaults mirror the legacy FormRenderer defaultValue semantics —
  // empty string for text-likes, empty array for multi-select, false for
  // checkbox, null otherwise.
  const defaults = useMemo<Record<string, unknown>>(() => {
    const out: Record<string, unknown> = {};
    for (const section of definition.sections) {
      for (const field of section.fields) {
        if (field.kind === "section_heading") continue;
        switch (field.kind) {
          case "text":
          case "long_text":
          case "email":
          case "date":
          case "partner_lookup":
            out[field.name] = "";
            break;
          case "number":
            out[field.name] = null;
            break;
          case "select":
            out[field.name] = field.multiple ? [] : "";
            break;
          case "checkbox":
            out[field.name] = false;
            break;
          case "file":
            out[field.name] = null;
            break;
        }
      }
    }
    return out;
  }, [definition]);

  const rhf = useForm<Record<string, unknown>>({
    resolver: zodResolver(schema as never),
    defaultValues: defaults as DefaultValues<Record<string, unknown>>,
    mode: "onBlur",
  });

  const [pending, setPending] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // SEC3: Turnstile token (only relevant when a site key is configured).
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const resetTurnstileRef = useRef<(() => void) | null>(null);
  const turnstileEnabled = Boolean(TURNSTILE_SITE_KEY);

  async function onSubmit(values: Record<string, unknown>) {
    setSubmitError(null);
    if (turnstileEnabled && !turnstileToken) {
      setSubmitError("Please complete the verification challenge below.");
      return;
    }
    setPending(true);
    try {
      // Lift any email-like value into submitter_email so the worker can
      // satisfy form_submissions_identity_check (0029): anonymous submits
      // need partner_id OR submitter_email. Both `kind === "email"` and
      // `kind === "partner_lookup"` (when the value looks like an email)
      // qualify. First match wins so an explicit email field beats a
      // partner_lookup fallback.
      let submitterEmail: string | undefined;
      for (const section of definition.sections) {
        for (const field of section.fields) {
          const v = values[field.name];
          if (typeof v !== "string" || v.length === 0) continue;
          if (field.kind === "email") {
            submitterEmail = v;
            break;
          }
          if (field.kind === "partner_lookup" && v.includes("@")) {
            submitterEmail = v;
          }
        }
        if (submitterEmail) break;
      }
      const result = await submitEmbedForm(slug, values, {
        submitterEmail,
        turnstileToken: turnstileToken ?? undefined,
      });
      if (!result.ok) {
        if (result.issues && result.issues.length > 0) {
          for (const issue of result.issues) {
            const path = issue.path?.[0];
            if (typeof path === "string") {
              rhf.setError(path, { message: issue.message });
            }
          }
        }
        setSubmitError(result.error);
        // A failed/expired challenge must be re-solved before the next try.
        resetTurnstileRef.current?.();
        return;
      }
      setSuccess(result.id);
    } finally {
      setPending(false);
    }
  }

  if (success !== null) {
    return (
      <div className="mx-auto max-w-lg rounded-xl border bg-card p-8 text-center">
        <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-status-active/15 text-status-active">
          ✓
        </div>
        <h2 className="font-display text-xl font-semibold tracking-tight">
          {definition.title} submitted
        </h2>
        {success ? (
          <p className="mt-2 text-sm text-muted-foreground">
            Reference:{" "}
            <code className="font-mono text-xs text-foreground">
              {success.slice(0, 8).toUpperCase()}
            </code>
          </p>
        ) : null}
        <p className="mt-1 text-sm text-muted-foreground">
          Thanks — we&apos;ve received your submission and will respond shortly.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={rhf.handleSubmit(onSubmit)}
      className="mx-auto max-w-2xl space-y-6"
    >
      {definition.description ? (
        <p className="text-sm text-muted-foreground">{definition.description}</p>
      ) : null}

      {definition.sections.map((section, sIdx) => (
        <div key={section.id ?? sIdx} className="space-y-4">
          {section.heading ? (
            <div className="border-b border-border/70 pb-2">
              <h3 className="font-display text-base font-semibold tracking-tight text-foreground">
                {section.heading}
              </h3>
              {section.description ? (
                <p className="mt-1 text-sm text-muted-foreground">
                  {section.description}
                </p>
              ) : null}
            </div>
          ) : null}
          {section.fields.map((field) => {
            // Embed has no attachment surface — render a notice instead of
            // the file-upload control. Legacy embed surface didn't support
            // attachments either.
            if (field.kind === "file") {
              return (
                <div
                  key={field.id}
                  className="rounded-md border border-dashed bg-muted/30 px-3 py-3 text-sm text-muted-foreground"
                >
                  {field.label}: file uploads aren&apos;t supported via embeds —
                  contact us directly to share attachments.
                </div>
              );
            }
            const Renderer = FIELD_RENDERERS[field.kind];
            if (!Renderer) return null;
            return (
              <Renderer
                key={field.id}
                field={field}
                control={rhf.control}
                disabled={pending}
                embedContext
                slug={slug}
                submissionId={null}
              />
            );
          })}
        </div>
      ))}

      {submitError ? (
        <div
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {submitError}
        </div>
      ) : null}

      {turnstileEnabled ? (
        <TurnstileWidget
          onToken={setTurnstileToken}
          onReady={(reset) => {
            resetTurnstileRef.current = reset;
          }}
        />
      ) : null}

      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={pending}>
          {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {definition.submitButtonLabel ?? "Submit"}
        </Button>
      </div>
    </form>
  );
}
